import React, { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import * as S from './CollectionDesktopFilter.styles';
import { SearchInput, NothingFound } from './DesktopFilters.styles';
import { DesktopFilterAccordion } from '@/components/Filters/DesktopFilters/DesktopFilterAccordion';
import { Property, PropertyOption } from '@/api/types/categories.types';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { Collection } from '@/components/Filters/FiltersDrawer.types';
import { getSearchWithoutChildProperties } from '@/components/Filters/DesktopFilters/CollectionDesktopFilter.helpers';
import { CollectionDesktopFilterOption } from '@/components/Filters/DesktopFilters/CollectionDesktopFilterOption';
import { MIN_OPTIONS_TO_SEARCH } from '@/components/Filters/DesktopFilters/CollectionDesktopFilter.constants';
import { AccordionRef } from '@/components/UI/Accordion/Accordion';

interface Props {
  propertyLegacyId: string;
  options: PropertyOption[];
  collection: Collection;
  allProperties?: Property[];
  popularOptions?: PropertyOption[];
}

export const CollectionDesktopFilter: FC<Props> = ({
  propertyLegacyId,
  options,
  collection,
  allProperties,
  popularOptions,
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { clearUrl, queryString } = useUrlAndHashFromPath();
  const [searchValue, setSearchValue] = useState('');
  const accordionRef = useRef<AccordionRef>(null);

  const propertyValue = collection[propertyLegacyId];
  const name = allProperties?.find(
    el => el.legacyResourceId === propertyLegacyId
  )?.name;
  const selected = options.find(
    el => el.legacyResourceId.toString() === propertyValue
  );

  const searchParamsWithoutChildren = useMemo(
    () =>
      getSearchWithoutChildProperties({
        propertyLegacyId,
        queryString,
        allProperties,
      }),
    [propertyLegacyId, queryString, allProperties]
  );

  function handleClear() {
    const searchParams = new URLSearchParams(searchParamsWithoutChildren);
    searchParams.delete(`p[${propertyLegacyId}]`);
    router.push(`${clearUrl}?${searchParams}`, undefined, {
      scroll: false,
    });
  }

  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const withPopularOptions = !!popularOptions?.length && !searchValue;

  if (!name || !options.length) return null;

  return (
    <DesktopFilterAccordion
      ref={accordionRef}
      dataTestId={`collection-property-${propertyLegacyId}`}
      title={name}
      value={selected?.name}
      onClear={handleClear}
      search={
        options.length >= MIN_OPTIONS_TO_SEARCH && (
          <SearchInput
            $hasBottomMargin={!withPopularOptions}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            placeholder={t('common.search')}
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            type="search"
          />
        )
      }
    >
      {withPopularOptions && (
        <>
          <S.OptionsTitle>{t('common.popular')}</S.OptionsTitle>
          {popularOptions.map(option => (
            <CollectionDesktopFilterOption
              key={option.name}
              option={option}
              onClick={() => accordionRef.current?.close()}
              clearUrl={clearUrl}
              baseSearchParams={searchParamsWithoutChildren}
              propertyLegacyId={propertyLegacyId}
              value={collection?.[option.propertyLegacyId]}
            />
          ))}
          <S.OptionsTitle>{t('common.all')}</S.OptionsTitle>
        </>
      )}

      {filteredOptions.length > 0 ? (
        filteredOptions.map(option => (
          <CollectionDesktopFilterOption
            key={option.name}
            option={option}
            onClick={() => accordionRef.current?.close()}
            clearUrl={clearUrl}
            baseSearchParams={searchParamsWithoutChildren}
            propertyLegacyId={propertyLegacyId}
            value={collection?.[option.propertyLegacyId]}
          />
        ))
      ) : (
        <NothingFound>{t('common.nothing_found')}</NothingFound>
      )}
    </DesktopFilterAccordion>
  );
};
